import { createApi } from "../../../utils/createApi";
import { useSiteAboutStore } from "../../Sites/SiteAboutPage/siteAboutStore/siteAboutStore";
import { useFlexIgnoreZonesOnboardingStore } from "../../Sites/SiteAboutPage/FlexIgnoreZonesOnboarding/flexIgnoreZonesOnboardingStore";
import type {
  FlexCameraSummary,
  FlexScheduleSummary,
} from "../../Sites/SiteAboutPage/FlexIgnoreZonesOnboarding/types";

/** Exported for unit tests — same mapping as Site About flex sync. */
export const buildFlexCameraSummariesFromSiteResults = (
  results: any[]
): FlexCameraSummary[] =>
  results.map((camera: any) => ({
    id: Number(camera.id),
    camera_name: camera.camera_name,
    preview_url: camera.preview_url,
    ignore_zones: camera.ignore_zones,
  }));

/** Exported for unit tests — same shape as `flexSchedulesForSync` in SiteAbout. */
export const buildFlexScheduleSummariesFromApi = (
  data: unknown
): FlexScheduleSummary[] => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }
  return data
    .map((s: any, idx: number) => ({
      id: Number(s?.id ?? idx + 1),
      display_name: s?.display_name || `Schedule ${idx + 1}`,
    }))
    .filter((row) => Number.isFinite(row.id));
};

/**
 * Hydrates the flex onboarding store for Edit Camera / Edit Ignore Zones.
 *
 * When `focusCameraId` is set (normal for those routes), only that camera is passed into
 * flex sync so `preset/?camera=` and `loadCameraContext` run for that camera alone — not
 * every camera on the site. When omitted, paginates `camera/site/` and syncs all cameras
 * (legacy / broad hydration).
 */
export async function hydrateFlexIgnoreZonesForEditCameraPage(
  siteId: string | number,
  options?: { signal?: AbortSignal; focusCameraId?: number }
): Promise<void> {
  const signal = options?.signal;
  const focusCameraId = options?.focusCameraId;
  const AxiosUI = createApi("");

  let flexCameras: FlexCameraSummary[];

  if (
    focusCameraId != null &&
    Number.isFinite(Number(focusCameraId)) &&
    Number(focusCameraId) > 0
  ) {
    const id = Number(focusCameraId);
    flexCameras = [
      {
        id,
        camera_name: "",
        preview_url: "",
        ignore_zones: [],
      },
    ];
  } else {
    const allResults: any[] = [];
    let pageNumber = 1;
    let hasMore = true;

    while (hasMore) {
      if (signal?.aborted) {
        return;
      }
      const res = await AxiosUI.get(
        `camera/site/?customer__id=${siteId}&page=${pageNumber}`,
        { signal }
      );
      const chunk = res.data?.results;
      if (Array.isArray(chunk)) {
        allResults.push(...chunk);
      }
      if (res.data?.next) {
        pageNumber += 1;
      } else {
        hasMore = false;
      }
    }

    if (signal?.aborted) {
      return;
    }

    flexCameras = buildFlexCameraSummariesFromSiteResults(allResults);
  }

  let flexSchedules: FlexScheduleSummary[] = [];
  try {
    const schedRes = await AxiosUI.get(`flex_schedule/by_customer/${siteId}/`, {
      signal,
    });
    const raw = schedRes.data;
    useSiteAboutStore.getState().setSiteSchedules(raw);
    flexSchedules = buildFlexScheduleSummariesFromApi(raw);
  } catch {
    useSiteAboutStore.getState().setSiteSchedules([]);
  }

  if (signal?.aborted) {
    return;
  }

  await useFlexIgnoreZonesOnboardingStore.getState().syncFlexIgnoreZonesEnabledFromApi(
    Number(siteId),
    flexCameras,
    flexSchedules.length > 0 ? flexSchedules : undefined,
    true
  );
}
